import { createAsyncThunk } from "@reduxjs/toolkit";
import { ObjectTarget } from "../../app/permissions";
import { failedResponse, getBackendUrl } from "../common";
import { RootState } from "../../app/store";
import { UserInfo } from "../user/userSlice";

export enum ConversationType {
    SINGLE_PARTY        = 2,
    DUAL_PARTY          = 3,
	ENTITY_GROUP        = 6,
	GENERAL_GROUP       = 7,
}

export enum ConversationStatus {
    ENABLED              =  0,
	DISABLED			 =  1,
}

export enum ConversationUserStatus {
    ACTIVE              =  0,
	SILENT			    =  1,
	REMOVED			    = 13,
}

export interface ConversationPartyInfo {
    id: string;
    conversationId: string;
    objectTarget: ObjectTarget;
    targetId: string;
}

export function assertText(msg?: string) {
    return msg?.split("\n").map((line, i) => <span key={i}>{line}<br /></span>);
}

export interface ConversationMessage {
    id: string;
    conversationId: string;
    messageId: string;
    messageType: number;
    text?: string;
    contentId?: string;
    partyId?: string;
    userId?: string;
    processed: string;
    messagePriority: number;
    deleted: number;
    edited?: string;
    editUserId?: number;
}

export interface ConversationInfo {
    id: string;
    lastMessageId?: number;
    lastReadMessageId?: number;
    lastNonSystemId?: number;
    conversationStatus: ConversationStatus;
    conversationUserStatus: ConversationUserStatus;
    lastMessageDate?: string;
    conversationType: number;
    name: string;
    avatar: string;
    key: string;
    lastPulledMessageId?: number;
    parties: ConversationPartyInfo[];
}

export const determineImageParty = (conversation?: ConversationInfo): ConversationPartyInfo | undefined => {
    if (!conversation) {
        return undefined;
    }

    if(conversation.conversationType === ConversationType.SINGLE_PARTY) {
        if (conversation.parties.length === 0) {
            return undefined;
        }
        return conversation.parties[0];
    }

    if (conversation.conversationType === ConversationType.DUAL_PARTY) {
        for (const party of conversation.parties) {
            if (party.objectTarget === ObjectTarget.STUDENT) {
                return party;
            }
        }
        if (conversation.parties.length === 0) {
            return undefined;
        }
        return conversation.parties[0];
    }

    if (conversation.conversationType === ConversationType.ENTITY_GROUP) {
        for (const party of conversation.parties) {
            if (party.objectTarget === ObjectTarget.SCHOOL
                || party.objectTarget === ObjectTarget.CLASSROOM
            ) {
                return party;
            }
        }
        if (conversation.parties.length === 0) {
            return undefined;
        }
        return conversation.parties[0];
    }

    return undefined;
};

export const determineMe = (conversation?: ConversationInfo, user?: UserInfo): ConversationPartyInfo | undefined => {
    if (!conversation || !user) {
        return undefined;
    }
    for (const party of conversation.parties) {
        if (party.objectTarget === ObjectTarget.SCHOOL) {
            return party;
        }
        if (party.objectTarget === ObjectTarget.CLASSROOM) {
            return party;
        }
        if (party.objectTarget === ObjectTarget.DPTO) {
            return party;
        }
        if (party.objectTarget === ObjectTarget.USER && party.targetId === user.id) {
            return party;
        }
    }
    return undefined;
};

export const datetimeSimplifier = (strDate: string | undefined) => {
    const date = new Date(strDate || "2000-01-01" );
    const options: Intl.DateTimeFormatOptions = { year: "numeric", month: "short", day: "2-digit", hour12: false, hour: "2-digit", minute: "2-digit" };
    return date.toLocaleDateString(undefined, options);
};

export const timeSimplifier = (strDate: string | undefined) => {
    const date = new Date(strDate || "2000-01-01" );
    const options: Intl.DateTimeFormatOptions = { hour12: false, hour: "2-digit", minute: "2-digit" };
    return date.toLocaleTimeString(undefined, options);
};

export const dateSimplifier = (strDate: string | undefined) => {
    const date = new Date(strDate || "2000-01-01" );

    date.setHours(0, 0, 0, 0);

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (date.getTime() === today.getTime()) {
        return "Hoje";
    }

    today.setDate(today.getDate() - 1);
    if (date.getTime() === today.getTime()) {
        return "Ontem";
    }

    let options: Intl.DateTimeFormatOptions;
    today.setDate(today.getDate() - 5);
    if (date.getTime() > today.getTime()) {
        options = { weekday: "long" };
    } else {
        options = { year: "numeric", month: "short", day: "2-digit" };
    }

    return date.toLocaleDateString(undefined, options);
};

export interface RetrieveConversationRequest {
    conversationId: string;
}

export const retrieveConversation = createAsyncThunk(
    "retrieveConversation",
    async (param: RetrieveConversationRequest, thunkAPI) => {
        console.debug("retrieve conversation", param);
        const state = thunkAPI.getState() as RootState;
        const token = state.user.token;
        if (!token) {
            return thunkAPI.rejectWithValue({
                status: 401,
                message: "Unauthorized",
                error: "No token available"
            });
        }
        const url = `${getBackendUrl()}/api/conversation/get/${param.conversationId}`;
        // console.debug("calling backend", url, token);
        const response = await fetch(url, {
            method: "GET",
            signal: thunkAPI.signal,
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
            },
        });
        if (response.ok) {
            return await response.json();
        } else {
            return thunkAPI.rejectWithValue(failedResponse(response));
        }
    }
);
